import { createContext, useState, useContext, useCallback } from "react";
import axios from "axios";
import CryptoJS from "crypto-js";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import { fetchUserGeo } from "../utils/geo";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

const SECRET_KEY = process.env.REACT_APP_SECRET_KEY;

const encryptData = (data) => CryptoJS.AES.encrypt(data, SECRET_KEY).toString();

export const AuthProvider = ({ children }) => {
  const [successful, setSuccessful] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  //Navigation
  const navigate = useNavigate();

  const login = useCallback(async (email, password) => {
    setLoading(true);
    setErr("");

    try {
      const geo = await fetchUserGeo();
      const storeId = geo.storeId || 1;

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URI}/authentication/login`,
        { email, password, storeId }
      );

      const { userId, accessToken, refreshToken, roles } = response.data;

      if (response.status === 200) {
        const userResponse = await axios.get(
          `${process.env.REACT_APP_BASE_URI}/users/${userId}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );

        const academicLevel = userResponse.data.academicLevel;
        const userName = userResponse.data.name;

        localStorage.setItem("userId", encryptData(userId));
        localStorage.setItem("userName", userName);
        localStorage.setItem("academiclvl", encryptData(academicLevel));
        localStorage.setItem("accessToken", encryptData(accessToken));
        localStorage.setItem("refreshToken", encryptData(refreshToken));
        localStorage.setItem("role", roles);

        sessionStorage.setItem("justLoggedIn", "true");
        window.dispatchEvent(new Event("storage"));

        return { success: true, roles, academicLevel };
      } else {
        setErr("Login failed. Please check your credentials.");
        return { success: false };
      }
    } catch (error) {
      const message =
        error.response?.data?.detail || "An unexpected error occurred.";
      setErr(message);
      toast.error(message);
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const signup = async (payload) => {
    setLoading(true);
    setErr("");

    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URI}/authentication/register`,
        payload
      );

      setSuccessful(true);
      return true;
    } catch (error) {
      const message =
        error.response?.data?.detail || "An unexpected error occurred.";
      setErr(message);
      toast.error(message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.clear();
    window.dispatchEvent(new Event("storage"));
    navigate("/login", { replace: true });
  };

  return (
    <AuthContext.Provider
      value={{ login, signup, logout, err, loading, successful }}
    >
      {children}
    </AuthContext.Provider>
  );
};
